import { useEffect } from 'react';
import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { SearchInput } from '../../generic';
import * as ProjectsV2Store from '../../../redux/projectsV2';
import useFetchProjectsAction from './hooks/useFetchProjectsAction';
import { useDebouncedCallback, useInputValue } from '../../../hooks';

type Props = {
  isReleaseGroups: boolean;
};

const ProjectsSearch: VoidFunctionComponent<Props> = ({ isReleaseGroups }) => {
  const title = useSelector(ProjectsV2Store.selectors.getTitle);
  const onSearchChange = useFetchProjectsAction(ProjectsV2Store.actions.setTitle);

  // Even though this is debounced internally, we debounce here to prevent hammering the Redux store and causing rerenders.
  const debouncedOnSearchChange = useDebouncedCallback(onSearchChange);

  //  We are aware that we are duplicating state here.
  const [value, setSearch] = useInputValue(title ?? '');
  //  Catch changes to the Redux store. Again, duplicating state is not a good pattern,
  //  but we wanted to avoid hammering actions to the Redux store.
  useEffect(() => {
    setSearch(title ?? '');
  }, [title, setSearch]);

  const onChange = (nextValue: string) => {
    //  Reflect the changes in the state right away...
    setSearch(nextValue);
    //  ...then send a debounced update to the Redux store.
    debouncedOnSearchChange(nextValue);
  };

  return (
    <SearchInput
      label="projects-search"
      placeholder={isReleaseGroups ? 'Search by release group name' : 'Search by project name'}
      value={value}
      onChange={onChange}
    />
  );
};

export default ProjectsSearch;
